import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import CumulativeChartTooltip from "./CumulativeChartTooltip";
import { useTheme } from "@mui/material/styles";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
  type ScriptableContext,
  type TooltipModel,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MasteryEvent {
  /** Index in `labels` at which this mastery occurred */
  weekIndex: number;
  /** Name of the target that was mastered */
  targetName: string;
}

export interface GoalCumulativeRecordChartProps {
  /** Week labels for the x-axis, e.g. ["W0", "W1", …, "W15"] */
  labels: string[];
  /** Cumulative count of targets mastered at each week — parallel to labels */
  data: (number | null)[];
  /** Total number of targets — sets the Y-axis max */
  totalTargets: number;
  /** Summary text rendered in the heading, e.g. "2 of 3 targets mastered" */
  masteredSummary?: string;
  /** Mastery milestones annotated with dots and enriched tooltips */
  masteryEvents?: MasteryEvent[];
  /** Label indices where a clinical phase transition occurs — draws a vertical divider line */
  phaseMarkers?: number[];
  /** Fires with the underlying Chart.js instance on mount, and with null on unmount. */
  onChartReady?: (chart: ChartJS<"line"> | null) => void;
  /** Optional line/fill override; defaults to success green. */
  color?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function createAreaGradient(
  ctx: CanvasRenderingContext2D,
  chartArea: { top: number; bottom: number },
  color: string
): CanvasGradient {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, `${color}40`); // ~25% opacity
  gradient.addColorStop(1, `${color}08`); // ~3% opacity
  return gradient;
}

function buildPhaseMarkerPlugin(phaseMarkers: number[], dividerColor: string): Plugin<"line"> {
  return {
    id: "cumulativePhaseMarkers",
    afterDraw(chart) {
      if (!phaseMarkers.length) return;
      const { ctx, scales, chartArea } = chart;
      const xScale = scales["x"];
      if (!xScale || !chartArea) return;

      const tickCount = xScale.ticks.length;

      ctx.save();
      ctx.strokeStyle = dividerColor;
      ctx.lineWidth = 1;

      phaseMarkers.forEach((weekIndex) => {
        // Bounds-check: skip indices outside the rendered tick range
        if (weekIndex < 0 || weekIndex >= tickCount) return;

        // CategoryScale tick positions must be retrieved via getPixelForTick,
        // not getPixelForValue — the latter expects the string label value.
        const x = xScale.getPixelForTick(weekIndex);
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
      });

      ctx.restore();
    },
  };
}

// ---------------------------------------------------------------------------
// Tooltip state
// ---------------------------------------------------------------------------

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  weekLabel: string;
  countLabel: string;
  masteredTarget?: string;
}

const HIDDEN_TOOLTIP: TooltipState = {
  visible: false,
  x: 0,
  y: 0,
  weekLabel: "",
  countLabel: "",
};

function isSameTooltip(next: TooltipState, current: TooltipState): boolean {
  return (
    next.visible === current.visible &&
    next.x === current.x &&
    next.y === current.y &&
    next.weekLabel === current.weekLabel &&
    next.countLabel === current.countLabel &&
    next.masteredTarget === current.masteredTarget
  );
}

// ---------------------------------------------------------------------------
// GoalCumulativeRecordChart
// ---------------------------------------------------------------------------

/**
 * Full-panel cumulative mastery chart for the GoalDetail view.
 *
 * Renders a stepped line showing how many targets have been mastered over
 * time. Mastery milestones are annotated with dots and a contextual tooltip.
 * Optional phase transition markers are drawn as vertical divider lines.
 *
 * @param props
 * @param props.labels          - Week labels, e.g. ["W0", "W1", …, "W15"]
 * @param props.data            - Cumulative mastered count per week
 * @param props.totalTargets    - Total targets (Y-axis max)
 * @param props.masteredSummary - Optional heading subtitle
 * @param props.masteryEvents   - Individual mastery milestones
 * @param props.phaseMarkers    - Week indices for phase transition dividers
 */
const GoalCumulativeRecordChart: React.FC<GoalCumulativeRecordChartProps> = ({
  labels,
  data,
  totalTargets,
  masteredSummary,
  masteryEvents = [],
  phaseMarkers = [],
  onChartReady,
  color,
}) => {
  const theme = useTheme();
  const chartRef = useRef<ChartJS<"line"> | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>(HIDDEN_TOOLTIP);

  const setChartRef = useCallback(
    (chart: ChartJS<"line"> | null | undefined) => {
      const normalized = chart ?? null;
      chartRef.current = normalized;
      onChartReady?.(normalized);
    },
    [onChartReady]
  );

  // Cleanup the Chart instance
  useEffect(() => {
    return () => {
      chartRef.current?.destroy();
    };
  }, []);

  const p = theme.palette as any;
  const lineColor: string = color ?? p.green?.[500] ?? "#4CAF50";
  const axisColor: string = p.grayscale?.[600] ?? "#798397";

  // Set of week indices that have a mastery event — used for per-point radius
  const masteryWeekSet = useMemo(
    () => new Set(masteryEvents.map((e) => e.weekIndex)),
    [masteryEvents]
  );

  // Dynamic gradient fill
  const getBackgroundColor = useCallback(
    (context: ScriptableContext<"line">) => {
      const { chart } = context;
      const { ctx, chartArea } = chart;
      if (!chartArea) return "transparent";
      return createAreaGradient(ctx, chartArea, lineColor);
    },
    [lineColor]
  );

  // External tooltip handler — bridges Chart.js hover state into React
  const handleExternalTooltip = useCallback(
    (context: { chart: ChartJS<"line">; tooltip: TooltipModel<"line"> }) => {
      const { tooltip: tip } = context;

      if (tip.opacity === 0) {
        setTooltip((prev) => (prev.visible ? HIDDEN_TOOLTIP : prev));
        return;
      }

      const dataIndex = tip.dataPoints[0]?.dataIndex ?? -1;
      const count = dataIndex >= 0 ? data[dataIndex] : null;
      const event = masteryEvents.find((e) => e.weekIndex === dataIndex);

      const newTooltip: TooltipState = {
        visible: true,
        x: tip.caretX,
        y: tip.caretY,
        weekLabel: tip.title?.[0] ?? labels[dataIndex] ?? "",
        countLabel: count != null ? `${count} of ${totalTargets} targets mastered` : "",
        masteredTarget: event?.targetName,
      };

      // Only update state when values actually change to avoid infinite
      // update loops triggered by Chart.js calling the external handler
      // frequently with identical data.
      setTooltip((prev) => (isSameTooltip(newTooltip, prev) ? prev : newTooltip));
    },
    [data, labels, masteryEvents, totalTargets]
  );

  const chartData: ChartData<"line"> = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data,
          borderColor: "transparent",
          borderWidth: 0,
          stepped: true,
          fill: "origin" as const,
          backgroundColor: getBackgroundColor as any,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          order: 2,
        },
        // Layer 2 — stepped line + mastery dots (rendered on top of the fill)
        {
          data,
          borderColor: lineColor,
          borderWidth: 2,
          stepped: true,
          fill: false as const,
          pointRadius: (ctx: ScriptableContext<"line">) =>
            masteryWeekSet.has(ctx.dataIndex) ? 5 : 0,
          pointHoverRadius: (ctx: ScriptableContext<"line">) =>
            masteryWeekSet.has(ctx.dataIndex) ? 6 : 4,
          pointBackgroundColor: lineColor,
          pointBorderColor: theme.palette.background?.paper ?? "#fff",
          pointBorderWidth: 1.5,
          tension: 0,
          order: 1,
        },
      ],
    }),
    [labels, data, lineColor, getBackgroundColor, masteryWeekSet, theme]
  );

  const chartOptions: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      animation: false,
      layout: { padding: { top: 8, right: 8, bottom: 0, left: 0 } },
      scales: {
        x: {
          display: true,
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: axisColor,
            font: { size: 12, family: "BasierCircle, sans-serif" },
            maxRotation: 0,
            padding: 4,
          },
          title: {
            display: true,
            text: "Week",
            color: axisColor,
            font: { size: 12, family: "BasierCircle, sans-serif" },
          },
        },
        y: {
          display: true,
          min: 0,
          max: totalTargets,
          grid: { color: theme.palette.divider },
          border: { display: false },
          ticks: {
            stepSize: 1,
            color: axisColor,
            font: { size: 12, family: "BasierCircle, sans-serif" },
            padding: 4,
          },
          title: {
            display: true,
            text: "Targets Mastered",
            color: axisColor,
            font: { size: 12, family: "BasierCircle, sans-serif" },
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: handleExternalTooltip as any,
        },
      },
    }),
    [axisColor, totalTargets, theme, handleExternalTooltip]
  );

  const phasePlugin = useMemo(
    () => buildPhaseMarkerPlugin(phaseMarkers, theme.palette.divider),
    [phaseMarkers, theme.palette.divider]
  );

  return (
    <Box
      component="section"
      aria-label="Cumulative record chart"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        bgcolor: "background.paper",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        px: 5,
        pt: 7,
        pb: 5,
        width: "100%",
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.primary">
          Cumulative Record
        </Typography>
        {masteredSummary && (
          <Typography variant="caption" sx={{ color: axisColor }}>
            {masteredSummary}
          </Typography>
        )}
      </Box>

      {/* ── Chart area ── */}
      <Box aria-label="Chart area" sx={{ position: "relative", width: "100%", height: 280 }}>
        <Line ref={setChartRef} data={chartData} options={chartOptions} plugins={[phasePlugin]} />

        {/* ── Custom floating tooltip ── */}
        {tooltip.visible && (
          <CumulativeChartTooltip
            x={tooltip.x}
            y={tooltip.y}
            weekLabel={tooltip.weekLabel}
            countLabel={tooltip.countLabel}
            masteredTarget={tooltip.masteredTarget}
          />
        )}
      </Box>
    </Box>
  );
};

export default GoalCumulativeRecordChart;
